// Primitivas reutilizables de la capa de marketing (web pública).
// Comparten el sistema de diseño "Audiophile Precision": fondo carbón, acentos
// cian (primary) y ámbar (vacuum-glow), tipografía Syne + JetBrains Mono.
//
// A diferencia del layout interno (ancho acotado a max-w-2xl, pensado para PWA
// móvil), la web pública usa un contenedor ancho de escritorio (hasta ~1240px)
// para no "encogerse" en pantallas grandes, respetando el documento de diseño
// (expansión a workstation en desktop).

import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '../ui/components.js';

/** Contenedor ancho centrado para secciones de la web pública. */
export function Container({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`w-full max-w-[1240px] mx-auto px-margin-mobile lg:px-margin ${className}`}>{children}</div>;
}

/** Píldora "eyebrow" (etiqueta superior de sección). */
export function Eyebrow({ children, icon }: { children: ReactNode; icon?: string }) {
  return (
    <span className="inline-flex items-center gap-space-xs px-3 py-1 rounded-full bg-primary-container/10 border border-primary-container/40 text-primary font-label-caps text-label-caps uppercase tracking-widest">
      {icon && <Icon name={icon} className="text-[14px]" />}
      {children}
    </span>
  );
}

/** Encabezado de sección centrado: eyebrow + título + subtítulo. */
export function SectionHeading({
  eyebrow,
  eyebrowIcon,
  title,
  subtitle,
  align = 'center',
}: {
  eyebrow?: string;
  eyebrowIcon?: string;
  title: ReactNode;
  subtitle?: ReactNode;
  align?: 'center' | 'left';
}) {
  const alignCls = align === 'center' ? 'items-center text-center mx-auto' : 'items-start text-left';
  return (
    <div className={`flex flex-col gap-space-md max-w-3xl ${alignCls}`}>
      {eyebrow && <Eyebrow icon={eyebrowIcon}>{eyebrow}</Eyebrow>}
      <h2 className="font-headline-lg text-headline-lg-mobile lg:text-headline-lg text-on-surface font-bold tracking-tight text-balance">
        {title}
      </h2>
      {subtitle && (
        <p className="font-body-md text-body-md lg:text-body-lg text-on-surface-variant leading-relaxed">{subtitle}</p>
      )}
    </div>
  );
}

/** Botón-enlace primario (CTA cian "vacuum-glow"). */
export function CtaPrimary({
  to,
  children,
  icon = 'arrow_forward',
  className = '',
}: {
  to: string;
  children: ReactNode;
  icon?: string;
  className?: string;
}) {
  return (
    <Link to={to} className={`btn-cyan ${className}`}>
      {children}
      <Icon name={icon} className="text-[20px]" />
    </Link>
  );
}

/** Botón-enlace secundario (superficie oscura, borde sutil). */
export function CtaSecondary({
  to,
  children,
  icon,
  className = '',
}: {
  to: string;
  children: ReactNode;
  icon?: string;
  className?: string;
}) {
  return (
    <Link to={to} className={`btn-secondary ${className}`}>
      {icon && <Icon name={icon} className="text-[18px]" />}
      {children}
    </Link>
  );
}

/**
 * Tarjeta de característica: icono (siempre azul) y título en la misma línea,
 * con la descripción debajo.
 * La prop `accent` se mantiene por compatibilidad pero ya no afecta el color.
 */
export function FeatureCard({
  icon,
  title,
  children,
}: {
  icon: string;
  title: string;
  children: ReactNode;
  accent?: 'cyan' | 'amber';
}) {
  return (
    <div className="surface-card flex flex-col gap-space-sm h-full hover:border-primary-container/50 transition-colors">
      <div className="flex items-center gap-space-sm">
        <span className="w-12 h-12 shrink-0 rounded-xl flex items-center justify-center border text-primary bg-primary-container/10 border-primary-container/30">
          <Icon name={icon} className="text-[24px]" />
        </span>
        <h3 className="font-headline-sm text-headline-md text-on-surface font-bold">{title}</h3>
      </div>
      <p className="font-body-sm text-body-sm text-on-surface-variant leading-relaxed">{children}</p>
    </div>
  );
}

/** Métrica/estadística para la prueba social. */
export function Stat({ value, label }: { value: string; label: string }) {
  return (
    <div className="flex flex-col items-center text-center gap-space-xs">
      <span className="font-headline-lg text-headline-lg text-primary font-bold tracking-tight">{value}</span>
      <span className="font-label-caps text-label-caps text-on-surface-variant uppercase tracking-widest">{label}</span>
    </div>
  );
}

/** Ítem de FAQ (acordeón nativo con <details>). */
export function FaqItem({ q, a }: { q: string; a: ReactNode }) {
  return (
    <details className="group accordion overflow-hidden">
      <summary className="accordion-header cursor-pointer list-none select-none">
        <span className="text-left font-headline-sm text-headline-sm text-on-surface">{q}</span>
        <Icon
          name="expand_more"
          className="text-[22px] text-on-surface-variant transition-transform group-open:rotate-180 shrink-0"
        />
      </summary>
      <div className="p-space-md border-t border-border-subtle font-body-sm text-body-sm text-on-surface-variant leading-relaxed">
        {a}
      </div>
    </details>
  );
}

/** Franja CTA de cierre, reutilizable al final de cada página. */
export function CtaBanner({
  title,
  subtitle,
  primaryTo = '/registro',
  primaryLabel,
  secondaryTo = '/precios',
  secondaryLabel,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  primaryTo?: string;
  primaryLabel?: string;
  secondaryTo?: string;
  secondaryLabel?: string;
}) {
  const { t } = useTranslation();
  const primary = primaryLabel ?? t('marketing.cta.createAccountFree');
  const secondary = secondaryLabel ?? t('marketing.cta.viewPlans');
  return (
    <section className="py-space-xl">
      <Container>
        <div className="relative overflow-hidden rounded-xl border border-primary-container/30 bg-surface-deck px-margin-mobile lg:px-margin py-space-xl flex flex-col items-center text-center gap-space-md shadow-[0_0_40px_rgba(56,189,248,0.12)]">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(56,189,248,0.18),transparent_60%)] pointer-events-none" />
          <div className="relative flex flex-col items-center gap-space-md">
            <h2 className="font-headline-lg text-headline-lg-mobile lg:text-headline-lg text-on-surface font-bold tracking-tight max-w-2xl text-balance">
              {title}
            </h2>
            {subtitle && (
              <p className="font-body-md text-body-md text-on-surface-variant max-w-xl leading-relaxed">{subtitle}</p>
            )}
            <div className="flex flex-col sm:flex-row items-center gap-space-sm mt-space-xs">
              <CtaPrimary to={primaryTo}>{primary}</CtaPrimary>
              <CtaSecondary to={secondaryTo}>{secondary}</CtaSecondary>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
}
